'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useResponsiveProp } from '@/lib/device/useBreakpoint'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { useIsReactNativeWebView } from '@/lib/react-native/contexts/react-native/ReactNativeProvider.client'
import { buildButtonClassName } from '@/ui/buttons/button-styles/buildButtonClassName'
import { ButtonContent } from '@/ui/buttons/button/_components/ButtonContent.client'
import { StatusDot } from '@/ui/status-dot/StatusDot'
import NextLink from 'next/link'
import { useRef } from 'react'
import { useFocusRing, useHover, useLink } from 'react-aria'
import { useLinkClickHandler } from '../_hooks/useLinkClickHandler'
import type { LinkButtonProps } from '../Link.types'
import { isTextLinkTone, toButtonStyleVariant } from '../linkVariantUtils'
import { getLinkUnderlineMode, isLinkToneVariant } from './linkButtonUnderlineClassName'

function LinkButtonInner(props: LinkButtonProps) {
  const ref = useRef<HTMLAnchorElement>(null)
  const isHydrated = useNextIsClientHydrated()
  const handleClick = useLinkClickHandler(props)

  const {
    href,
    opensInNewTab,
    className,
    variant = 'text-link',
    size,
    takesParentWidth,
    isDisabled,
    isLoading,
    underlined,
    bold,
    'aria-label': ariaLabel,
    'data-testid': testid = 'link',
    notification,
    notificationClassName,
    isRound: isRoundProp = false,
    iconLeft,
    iconRight,
    children,
    forceProgressBar: _forceProgressBar,
    onPress: _onPress,
    trackingData: _trackingData,
    intent: _intent,
    ...passthrough
  } = props

  const { isHovered, hoverProps } = useHover({
    isDisabled: Boolean(isDisabled),
  })
  const { isPressed, linkProps } = useLink({ isDisabled: Boolean(isDisabled || isLoading) }, ref)
  const { isFocusVisible, focusProps } = useFocusRing()
  const isRound = useResponsiveProp(isHydrated ? isRoundProp : false)
  const buttonStyleVariant = toButtonStyleVariant(variant)
  const isLinkTone = isTextLinkTone(variant)

  const finalClassName = buildButtonClassName({
    className,
    variant: buttonStyleVariant,
    size: size ?? 'md',
    isRound: isRoundProp,
    takesParentWidth,
    linkUnderlineMode:
      isLinkTone && isLinkToneVariant(buttonStyleVariant)
        ? getLinkUnderlineMode(underlined)
        : undefined,
  })

  const linkElement = (
    <NextLink
      ref={ref}
      {...passthrough}
      {...linkProps}
      {...focusProps}
      {...hoverProps}
      href={href}
      target={opensInNewTab ? '_blank' : undefined}
      data-testid={testid}
      className={finalClassName}
      data-hovered={isHovered ? 'true' : undefined}
      data-pressed={isPressed ? 'true' : undefined}
      data-focus-visible={isFocusVisible ? 'true' : undefined}
      aria-disabled={isDisabled || undefined}
      aria-label={ariaLabel || (isHydrated && isRound ? children : undefined)}
      aria-busy={isLoading ?? false}
      data-loading={isLoading || undefined}
      onClick={handleClick}
    >
      <ButtonContent
        iconLeft={iconLeft}
        iconRight={iconRight}
        isLoading={isLoading}
        isRound={isRound}
        isLinkTone={isLinkTone}
        bold={bold}
      >
        {children ?? ''}
      </ButtonContent>
    </NextLink>
  )

  if (notification) {
    return (
      <span className="relative block shrink-0">
        {linkElement}
        <StatusDot
          variant="info"
          className={twMerge(notificationClassName, 'absolute right-[0.4375rem] top-[0.4375rem]')}
        />
      </span>
    )
  }

  return linkElement
}

export function LinkButton({ intent, ...props }: LinkButtonProps): React.ReactNode {
  const isInWebView = useIsReactNativeWebView()

  // ⚠️ Temporary: hide buttons without intent in the WebView until their specification is ready,
  // to avoid displaying TODO alerts in production
  if (isInWebView && !intent && process.env.NODE_ENV === 'production') {
    return null
  }

  return <LinkButtonInner intent={intent} {...props} />
}
